import type { MouseEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface Params {
  expandTrigger?: string;
  collapseTrigger?: string | number;
  onClose?: () => void;
  opened?: boolean;
}

export function useDialog({ expandTrigger, collapseTrigger, onClose, opened = true }: Params) {
  const [expanded, setExpanded] = useState<boolean>(opened);
  const [shouldRender, setShouldRender] = useState<boolean>(opened);
  const hideTimeoutRef = useRef<number | null>(null);
  const prevExpandTriggerRef = useRef<string | undefined>(expandTrigger);
  const prevCollapseTriggerRef = useRef<string | number | undefined>(collapseTrigger);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const touchListenerCleanupRef = useRef<(() => void) | null>(null);

  const handleRef = useCallback((element: HTMLElement | null) => {
    if (touchListenerCleanupRef.current) {
      touchListenerCleanupRef.current();
      touchListenerCleanupRef.current = null;
    }

    if (!element) return;

    const onTouchStart = (event: TouchEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setExpanded((prev) => !prev);
    };

    element.addEventListener('touchstart', onTouchStart, { passive: false });
    touchListenerCleanupRef.current = () => element.removeEventListener('touchstart', onTouchStart);
  }, []);

  useEffect(() => {
    if (expandTrigger !== undefined && expandTrigger !== prevExpandTriggerRef.current) {
      setExpanded(true);
    }
    prevExpandTriggerRef.current = expandTrigger;
  }, [expandTrigger]);

  useEffect(() => {
    if (opened) {
      setShouldRender(true);
      setExpanded(true);
    } else {
      setExpanded(false);
      hideTimeoutRef.current = window.setTimeout(() => {
        setShouldRender(false);
      }, 300);
    }

    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [opened]);

  const collapse = useCallback(() => {
    setExpanded(false);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (collapseTrigger !== undefined && collapseTrigger !== prevCollapseTriggerRef.current) {
      collapse();
    }
    prevCollapseTriggerRef.current = collapseTrigger;
  }, [collapseTrigger, collapse]);

  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setExpanded((prev) => !prev);
  }, []);

  return { expanded, shouldRender, dialogRef, handleRef, handleClick } as const;
}
